import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  CircularProgress,
  Alert,
  Chip,
  Button
} from '@mui/material';
import { Assignment, PictureAsPdf } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { protocolAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import AppHeader from '../components/AppHeader';

export default function ProtocolListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadProtocols();
  }, []);

  const loadProtocols = async () => {
    try {
      const result = await protocolAPI.list();
      if (result.success) {
        let allProtocols = result.protocols || [];
        
        // Filter by user role - nur Admin sieht alles
        if (user?.role !== 'admin') {
          // Normale Monteure sehen nur ihre eigenen Protokolle
          allProtocols = allProtocols.filter(protocol => 
            protocol.monteur === user?.username || protocol.monteur === user?.full_name
          );
        }
        
        setProtocols(allProtocols);
      }
    } catch (err) {
      setError('Fehler beim Laden der Protokolle');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPDF = (protocol) => {
    if (protocol.pdf_id) {
      window.open(protocolAPI.getPDF(protocol.pdf_id), '_blank');
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppHeader title="Gespeicherte Protokolle" showBack={true} backTo="/dashboard" />

      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : protocols.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Assignment sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Keine Protokolle vorhanden
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Erstellen Sie Ihr erstes Wartungsprotokoll
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/protocol/customer-search')}
            >
              Neues Protokoll erstellen
            </Button>
          </Paper>
        ) : (
          <Paper>
            <List>
              {protocols.map((protocol, index) => (
                <ListItem key={protocol._id || index} disablePadding>
                  <ListItemButton onClick={() => handleOpenPDF(protocol)}>
                    <PictureAsPdf sx={{ mr: 2, color: 'error.main' }} />
                    <ListItemText
                      primary={protocol.kunde || 'Unbekannter Kunde'}
                      secondary={
                        <>
                          {protocol.ku_nr && `Kd.-Nr.: ${protocol.ku_nr} • `}
                          {protocol.datum || 'Kein Datum'}
                          {protocol.hersteller && ` • ${protocol.hersteller}`}
                        </>
                      }
                    />
                    {protocol.email_sent && (
                      <Chip label="E-Mail gesendet" size="small" color="success" />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </Container>
    </Box>
  );
}